import React, { FC } from 'react';
import { useGetAllCampaignsQuery } from '../../api/campaignAPI';
import Button, { ButtonModeE } from '../../components/Button/Button';
import usePopup from '../../utils/hooks/usePopup';

import styles from './styles.module.scss';

type ChangeCampaignNumberFooterPropTypes = {
    projectId: number;
}

/**
 * The popup footer is built by the caller (DeliveryWizard) before the reassignment targets are
 * known, and the popup slice has no updatePopup reducer — so the footer cannot be revised after the
 * popup opens. It therefore subscribes to the same query as the body and decides for itself.
 *
 * Same endpoint + byte-identical args = one shared request, not two: RTK Query keys its cache by the
 * serialised argument, and body and footer mount together as two subscribers to one in-flight
 * request. Keep these args identical to ChangeCampaignNumberBody's, or this becomes a second
 * request and a footer that disagrees with the body.
 *
 * Loading  -> no buttons at all (the popup's own X is the way out, as in every popup).
 * Empty    -> "Cancel and close" only; there is nothing to save, and Save would target a
 *             `select-campaign` form the empty branch never renders — an inert button.
 * Has data -> unchanged.
 */
const ChangeCampaignNumberFooter: FC<ChangeCampaignNumberFooterPropTypes> = (props) => {
    const { projectId } = props;

    const { removePopup } = usePopup();
    // isLoading (first load), not isFetching — matching the body, so the two never disagree about
    // which state they are in. On an error `data` is undefined and the `= []` default puts the
    // footer in the empty state, consistent with the body's message (requirements §5).
    const { data: campaignsSO = [], isLoading } = useGetAllCampaignsQuery({ projectId, asSelectOptions: true }, { skip: !projectId });

    if (isLoading) return null;

    return (
        <div className={styles.footerPopupButtons}>
            <Button onClick={removePopup} mode={ButtonModeE.OUTLINED}>
                Cancel and close
            </Button>
            {!!campaignsSO.length && (
                <Button form='select-campaign' useGlobalLoading>
                    Save
                </Button>
            )}
        </div>
    );
};

export default ChangeCampaignNumberFooter;
